import { useCallback, useContext, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import styles from "./RHFRenderSelectedProduct.module.css";
import { Button, Checkbox, SkeletonBodyText, TextField } from "@shopify/polaris";
import { ProductObj, VariantsObj } from "../../apps/Bundles/Types";
import { BundleTypes, getProductsDynamicallyAndReturnNewProductsArray } from "../../apps/Bundles/constants";

const DragDropContextAny = DragDropContext as any;
const DroppableAny = Droppable as any;
const DraggableAny = Draggable as any;
import DragHandle from "../Icons/DragHandle";
import CloseIcon from "../Icons/CloseIcon";
import CustomModal from "../CustomModal/CustomModal";
import { AppConfigContext } from "../../context/AppConfigContext";
import { getFieldError, mergeProductsById } from "../../utilities/functions";
import { RenderVariantInfo } from "../../apps/Bundles/BundleBuilder/BundleBuilderBundle/BBProductCard";
import { PlusIcon, ProductIcon, XIcon } from "@shopify/polaris-icons";
import { InlineError, Thumbnail } from "@shopify/polaris";
import { useFormContext, useWatch } from "react-hook-form";
import { useVariantFieldDisabled } from "../FormFields";

const RHFRenderSelectedProduct = ({
    bundleLevel = "product",
    bundleType,
    customTitle = "",
    updateKey = "products",
    excludeProducts = [],
    hideQuantity = false,
    multiple = 10,
    products_type = "",
    showVariantPicker = false,
    showAlternateProducts = false,
    resourcePickerQuery = "status:active AND published_status:published",
    value,
    onChange,
    errorMessage,
}: {
    bundleLevel: "product" | "variant";
    bundleType: string;
    customTitle?: string;
    updateKey: string;
    excludeProducts?: any;
    hideQuantity?: boolean;
    multiple?: number | boolean;
    products_type?: string;
    showVariantPicker?: boolean;
    showAlternateProducts?: boolean;
    resourcePickerQuery?: string;
    /** Override: when provided (with `onChange`), reads from this instead of `updateKey`'s RHF field */
    value?: any[];
    /** Override: when provided, writes go here instead of `setValue(updateKey, ...)` — for callers that
     * manage the array as part of a larger whole-array write (e.g. useFieldArray's `replace()`). */
    onChange?: (value: any[]) => void;
    /** Explicit error to show, for callers using the `value`/`onChange` override (no RHF field to read from) */
    errorMessage?: string;
}) => {
    const {
        control,
        setValue: setFormValue,
        formState: { errors: formErrors },
    } = useFormContext();
    const watchedProducts = useWatch({
        control,
        name: updateKey,
        defaultValue: [],
    });
    const products = value !== undefined ? value : watchedProducts;
    const [renderArr, setRenderArr] = useState<any[]>(products);
    const isAbDisabled = useVariantFieldDisabled(updateKey);
    const fieldError = errorMessage ?? (value === undefined ? getFieldError(formErrors, updateKey) : undefined);

    const setValue = useCallback(
        (key: string, next: any[], options?: any) => {
            if (onChange) onChange(next);
            else setFormValue(key, next, options);
        },
        [onChange, setFormValue],
    );

    const context = useContext(AppConfigContext);
    const { sft } = context;

    useEffect(() => {
        setRenderArr(products);
    }, [products]);

    const moveCard = useCallback(
        (originalIdx: number, newIdx: number) => {
            if (isAbDisabled) return;
            const temp = [...renderArr];
            const draggedCard = { ...temp[originalIdx] };
            temp.splice(originalIdx, 1);
            temp.splice(newIdx, 0, draggedCard);
            setValue(updateKey, temp, { shouldDirty: true });
        },
        [renderArr, setValue, updateKey, isAbDisabled],
    );

    const removeProduct = (pId: string = "", vId: string = "") => {
        if (isAbDisabled) return;
        const temp: ProductObj[] = [...renderArr];
        let idx = -1;
        if (bundleLevel === "variant") {
            idx = temp.findIndex((p: ProductObj) => p.variants[0]?.id === vId);
        } else {
            idx = temp.findIndex((p) => p.id === pId);
        }
        if (idx >= 0) {
            temp.splice(idx, 1);
            setValue(updateKey, temp, { shouldDirty: true });
        }
    };

    const handleQuantity = (pId = "", vId = "", val: number) => {
        if (isAbDisabled) return;
        const temp: ProductObj[] = [...renderArr];
        let idx = -1;
        if (bundleLevel === "variant") {
            idx = temp.findIndex((p: ProductObj) => p.variants[0]?.id === vId);
        } else {
            idx = temp.findIndex((p) => p.id === pId);
        }

        if (idx >= 0) {
            temp[idx] = {
                ...temp[idx],
                quantity: val,
            };
            setValue(updateKey, temp, { shouldDirty: true });
        }
    };

    const [isFetchingRequest, setIsFetchingRequest] = useState(false);

    const handleProductSearch = async (initialQuery: string = "") => {
        if (isAbDisabled) return;
        let preSelected = [];
        if (bundleType === BundleTypes.STANDARD && bundleLevel === "variant") {
            const grouped = mergeProductsById(products || []);
            preSelected = grouped.map((product) => ({
                id: product.id,
                variants: product.variants?.map((variant) => ({ id: variant.id })) || [],
            }));
        } else {
            preSelected = products.map((product: ProductObj) => ({
                id: product?.id,
                variants: product?.variants.map((variant: VariantsObj) => ({ id: variant?.id })),
            }));
        }

        let selected: any = await shopify.resourcePicker({
            type: "product",
            multiple: multiple,
            query: initialQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: resourcePickerQuery,
                variants: showVariantPicker,
            },
        });

        if (selected) {
            const productsArr: ProductObj[] = [];

            if (excludeProducts.length > 0) {
                selected = selected.filter((temp: ProductObj) => {
                    const isExcluded = excludeProducts.some((excluded: any) => excluded.id === temp.id);
                    if (isExcluded) {
                        shopify.toast.show("The main product cannot be added to the offered product list.", {
                            isError: true,
                        });
                        return false;
                    }
                    return true;
                });
            }

            if (bundleLevel === "product") {
                selected?.flatMap((product: any) => {
                    const existingProduct = products.find((p: any) => p.id === product.id);
                    productsArr.push({
                        id: product?.id,
                        images: product?.images,
                        title: product?.title,
                        handle: product?.handle,
                        quantity: 1,
                        maxProductStock: product?.totalInventory,
                        selectedVariants: [],
                        subscriptions: [],
                        oneTimePurchase: true,
                        isSubscribed: false,
                        alternateProducts: existingProduct?.alternateProducts || [],
                        variants: product?.variants?.map((variant: VariantsObj) => ({
                            image: { ...variant.image },
                            id: variant?.id,
                            displayName: variant.displayName,
                            title: variant?.title?.toLowerCase() === "default title" ? product?.title : variant?.title,
                            price: variant?.price,
                            quantity: 1,
                            selectedOptions: variant?.selectedOptions,
                            copyPrice: variant.price,
                            compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                        })),
                        options: product?.options,
                    });
                });
            } else {
                selected?.flatMap((product: any) => {
                    product?.variants.forEach((variant: VariantsObj) => {
                        const existingProduct = products.find(
                            (p: any) => p.id === product.id && p.variants[0]?.id === variant.id,
                        );
                        productsArr.push({
                            id: product?.id,
                            images: [variant.image || product.images[0]],
                            title:
                                bundleType === BundleTypes.STANDARD
                                    ? product?.title
                                    : variant?.title?.toLowerCase() === "default title"
                                      ? product?.title
                                      : variant?.title,
                            handle: product.handle,
                            quantity: 1,
                            maxProductStock: product?.totalInventory,
                            selectedVariants: [],
                            subscriptions: [],
                            oneTimePurchase: true,
                            isSubscribed: false,
                            alternateProducts: existingProduct?.alternateProducts || [],
                            variants: [
                                {
                                    id: variant?.id,
                                    displayName: variant.displayName,
                                    title:
                                        variant?.title?.toLowerCase() === "default title"
                                            ? product?.title
                                            : variant?.title,
                                    price: variant.price,
                                    image: {},
                                    selectedOptions: variant.selectedOptions,
                                    copyPrice: variant.price,
                                    compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                                } as any,
                            ],
                            options: product?.options,
                        });
                    });
                });
            }

            try {
                setIsFetchingRequest(true);
                const newProductsArr = await getProductsDynamicallyAndReturnNewProductsArray(
                    productsArr,
                    sft,
                    false,
                    bundleType,
                );
                setValue(updateKey, newProductsArr, { shouldDirty: true });
                setIsFetchingRequest(false);
            } catch (error) {
                console._log(error);
                setIsFetchingRequest(false);
                setValue(updateKey, productsArr, { shouldDirty: true });
            }
        }
    };

    const [error, setError] = useState<string>("");

    const handleAlternateProduct = async (productId: string) => {
        if (isAbDisabled) return;
        const mainProduct = products.find((p: any) => p.id === productId);

        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: false,
            query: "",
            action: "select",
            filter: {
                query: "status:active AND published_status:published",
            },
        });

        if (!selected || selected.length === 0) return;

        const alternate = selected[0];

        if (mainProduct.alternateProducts?.some((p: any) => p.id === alternate.id) || alternate.id === mainProduct.id) {
            setError("This product is already added as an alternate or is the same as the main product.");
            return;
        }

        const updatedProducts = products.map((p: ProductObj) => {
            if (p.id === productId) {
                return {
                    ...p,
                    alternateProducts: [...(p.alternateProducts || []), alternate],
                };
            }
            return p;
        });

        setValue(updateKey, updatedProducts, { shouldDirty: true });
        setError("");
    };

    const handleRemoveAlternate = (mainId: string, altId: string) => {
        if (isAbDisabled) return;
        const temp = [...products];
        const mainProductIndex = temp.findIndex((p: any) => p.id === mainId);
        if (mainProductIndex !== -1) {
            const mainProduct = { ...temp[mainProductIndex] };
            const currentAlternates = Array.isArray(mainProduct.alternateProducts)
                ? [...mainProduct.alternateProducts]
                : [];
            const newAlternates = currentAlternates.filter((a: any) => a.id !== altId);
            mainProduct.alternateProducts = newAlternates;
            temp[mainProductIndex] = mainProduct;
            setValue(updateKey, temp, { shouldDirty: true });
        }
    };

    const onDropReferences = (res: any) => {
        if (isAbDisabled) return;
        if (!res?.destination?.droppableId || !res?.source?.droppableId) return;
        else {
            moveCard(res.source.index, res.destination.index);
        }
    };

    const getLabel = () => {
        if (bundleLevel === "product") {
            if (bundleType === "product_add_on") {
                return products_type === "main_product"
                    ? `Add Specific Product${multiple === 1 ? "" : "s"}`
                    : `Add Add-on Product${multiple === 1 ? "" : "s"}`;
            }

            return renderArr.length === multiple
                ? `Edit Product${multiple === 1 ? "" : "s"}`
                : `+ Add Product${multiple === 1 ? "" : "s"}`;
        }
        return "+ Add Variants";
    };

    return (
        <DragDropContextAny onDragEnd={onDropReferences}>
            <DroppableAny droppableId="droppable">
                {(provided: any) => (
                    <div
                        className={styles.headingsDiv}
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{
                            ...(isAbDisabled && {
                                pointerEvents: "none",
                                opacity: "0.5",
                            }),
                        }}
                    >
                        <div className={styles.productNameWrapper}>
                            {bundleType !== "mixmatch" && (
                                <div className={styles.productName} style={{ width: "max-content" }}>
                                    {`${customTitle || "Included"} ${
                                        bundleLevel.charAt(0).toUpperCase() + bundleLevel.slice(1)
                                    }${renderArr?.length > 1 ? "s" : ""}`}{" "}
                                </div>
                            )}

                            {!!products?.length && (
                                <Button
                                    onClick={() => handleProductSearch()}
                                    variant="secondary"
                                    disabled={isAbDisabled}
                                >
                                    {getLabel()}
                                </Button>
                            )}
                        </div>
                        {renderArr?.length ? (
                            <div className={styles.productsRender}>
                                {renderArr.map((product: any, pIdx: number) => {
                                    const showVariantName =
                                        product?.variants?.length === 1 &&
                                        product?.variants[0]?.title?.toLowerCase() !== product?.title?.toLowerCase()
                                            ? product?.variants[0]?.title
                                            : "";

                                    return (
                                        <DraggableAny
                                            key={product?.variants[0]?.id}
                                            draggableId={product?.variants[0]?.id + ""}
                                            index={pIdx}
                                            isDragDisabled={isAbDisabled}
                                        >
                                            {(provided: any, snapshot: any) => (
                                                <div
                                                    key={product?.variants[0]?.id}
                                                    className={showAlternateProducts ? styles.productWithConnector : ""}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "flex-start",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                        ...provided.draggableProps.style,
                                                        borderRadius: 10,
                                                        border: snapshot.isDragging
                                                            ? "1px solid black"
                                                            : "1px solid transparent",
                                                        backgroundColor: "white",
                                                        padding: "5px",
                                                    }}
                                                >
                                                    <div {...provided.dragHandleProps}>
                                                        <DragHandle />
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            width: "100%",
                                                            cursor: "default",
                                                        }}
                                                    >
                                                        <div className={styles.productMeta}>
                                                            <div className={styles.productName}>
                                                                <Thumbnail
                                                                    source={
                                                                        product?.images?.[0]?.originalSrc || ProductIcon
                                                                    }
                                                                    alt={product?.images?.[0]?.altText}
                                                                    size="small"
                                                                />
                                                                <div className={styles.productTitleWrapper}>
                                                                    <div
                                                                        style={{
                                                                            whiteSpace: "normal",
                                                                            cursor: "pointer",
                                                                        }}
                                                                        onClick={() => {
                                                                            if (shopify.config.shop) {
                                                                                window.open(
                                                                                    `https://${shopify.config.shop}/products/${product.handle}`,
                                                                                    "_blank",
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={styles.productTitle}
                                                                    >
                                                                        {product?.title}
                                                                    </div>

                                                                    <div style={{ fontSize: 11 }}>
                                                                        {product?.variants?.length > 1 ? (
                                                                            <div>
                                                                                <span
                                                                                    className={styles.variantsLabel}
                                                                                    onClick={() => {
                                                                                        handleProductSearch(
                                                                                            product?.title,
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    {`${product?.variants?.length} variants `}
                                                                                </span>
                                                                                included
                                                                            </div>
                                                                        ) : null}
                                                                        {bundleLevel === "variant" &&
                                                                            bundleType === BundleTypes.STANDARD && (
                                                                                <div
                                                                                    className={
                                                                                        styles.variantInfoContainer
                                                                                    }
                                                                                >
                                                                                    <RenderVariantInfo
                                                                                        product={
                                                                                            showVariantName
                                                                                                ? {
                                                                                                      ...product,
                                                                                                      title: showVariantName,
                                                                                                  }
                                                                                                : product
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                    </div>

                                                                    {bundleType !== BundleTypes.VOLUME_DISCOUNT &&
                                                                        !product?.subscriptions?.length &&
                                                                        isFetchingRequest && (
                                                                            <SkeletonBodyText lines={1} />
                                                                        )}

                                                                    <div style={{ fontSize: 11 }}>
                                                                        <ShowSubscriptions
                                                                            product={product}
                                                                            products={products}
                                                                            renderArr={renderArr}
                                                                            updateKey={updateKey}
                                                                            setValue={setValue}
                                                                            disabled={isAbDisabled}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className={styles.productQty}
                                                                style={{ marginTop: -25 }}
                                                            >
                                                                {[
                                                                    BundleTypes.BXGY,
                                                                    BundleTypes.STANDARD,
                                                                    BundleTypes.PRODUCT_ADDONS,
                                                                ].includes(bundleType as any) &&
                                                                    !hideQuantity && (
                                                                        <div
                                                                            style={{
                                                                                minWidth: 90,
                                                                                maxWidth: 90,
                                                                            }}
                                                                        >
                                                                            <TextField
                                                                                label={
                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: 12,
                                                                                            fontWeight: 450,
                                                                                            marginBottom: -3,
                                                                                        }}
                                                                                    >
                                                                                        Quantity
                                                                                    </div>
                                                                                }
                                                                                type="number"
                                                                                min={1}
                                                                                autoComplete="off"
                                                                                onChange={(val) => {
                                                                                    const regex = /^\d*$/;
                                                                                    if (!regex.test(val)) return;
                                                                                    handleQuantity(
                                                                                        product?.id,
                                                                                        product.variants[0]?.id,
                                                                                        +val,
                                                                                    );
                                                                                }}
                                                                                size="slim"
                                                                                value={(+product.quantity).toString()}
                                                                                onBlur={() => {
                                                                                    if (+product.quantity <= 0) {
                                                                                        handleQuantity(
                                                                                            product?.id,
                                                                                            product.variants[0]?.id,
                                                                                            1,
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                {!isAbDisabled && (
                                                                    <div
                                                                        onClick={() => {
                                                                            removeProduct(
                                                                                product?.id,
                                                                                product?.variants[0]?.id,
                                                                            );
                                                                        }}
                                                                        style={{
                                                                            cursor: "pointer",
                                                                            display: "flex",
                                                                            justifyContent: "center",
                                                                            alignItems: "center",
                                                                            marginTop: "22px",
                                                                        }}
                                                                    >
                                                                        <CloseIcon />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className={styles.variantsWrapper}></div>
                                                        {showAlternateProducts && (
                                                            <div className={styles.conditionWrapper}>
                                                                <p className={styles.paragraph}>
                                                                    If product is in the order?
                                                                </p>

                                                                {product?.alternateProducts?.map((alt: any) => (
                                                                    <div key={alt.id} className={styles.upperAltDiv}>
                                                                        <div className={styles.singleProductAlt}>
                                                                            <div className={styles.singleProductLeft}>
                                                                                <Thumbnail
                                                                                    source={
                                                                                        alt.images[0]?.originalSrc ||
                                                                                        ProductIcon
                                                                                    }
                                                                                    alt={alt.images[0]?.altText}
                                                                                    size="extraSmall"
                                                                                />
                                                                                <span className={styles.altTitle}>
                                                                                    {alt.title}
                                                                                </span>
                                                                            </div>
                                                                            {!isAbDisabled && (
                                                                                <div
                                                                                    className={
                                                                                        styles.singleProductRight
                                                                                    }
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleRemoveAlternate(
                                                                                            product.id,
                                                                                            alt.id,
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <XIcon
                                                                                        height={12}
                                                                                        width={12}
                                                                                        fill="#616161"
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}

                                                                {(product.alternateProducts?.length ?? 0) < 1 && (
                                                                    <div style={{ width: "170px" }}>
                                                                        <Button
                                                                            size="micro"
                                                                            icon={PlusIcon}
                                                                            disabled={isAbDisabled}
                                                                            onClick={
                                                                                ((e: any) => {
                                                                                    e.stopPropagation();
                                                                                    handleAlternateProduct(product.id);
                                                                                }) as any
                                                                            }
                                                                        >
                                                                            Show another product
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </DraggableAny>
                                    );
                                })}
                                {error && (
                                    <div style={{ margin: "10px 0" }}>
                                        <InlineError message={error} fieldID="alternateProduct" />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div
                                className={styles.emptyProductBox}
                                onClick={() => handleProductSearch()}
                                style={{ ...(isAbDisabled && { pointerEvents: "none", opacity: "0.5" }) }}
                            >
                                <Button variant="secondary" loading={isFetchingRequest} disabled={isAbDisabled}>
                                    {getLabel()}
                                </Button>
                            </div>
                        )}
                        {fieldError && (
                            <div style={{ marginTop: "6px" }}>
                                <InlineError message={fieldError} fieldID={updateKey} />
                            </div>
                        )}
                    </div>
                )}
            </DroppableAny>
        </DragDropContextAny>
    );
};

export default RHFRenderSelectedProduct;

const ShowSubscriptions = ({
    product,
    renderArr,
    products,
    updateKey,
    setValue,
    disabled = false,
}: {
    product: any;
    renderArr: any;
    products: any;
    updateKey: any;
    setValue: any;
    disabled?: boolean;
}) => {
    const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);

    return (
        <>
            {product?.subscriptions?.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                    <p className={styles.variantsLabel} onClick={() => setOpenSubscriptionModal(true)}>
                        {product?.subscriptions?.length} subscriptions
                    </p>
                    &nbsp;included
                </div>
            ) : null}

            {openSubscriptionModal && (
                <SubscriptionModal
                    productSub={product}
                    setOpenSubscriptionModal={setOpenSubscriptionModal}
                    renderArr={renderArr}
                    products={products}
                    updateKey={updateKey}
                    setValue={setValue}
                    disabled={disabled}
                />
            )}
        </>
    );
};

const SubscriptionModal = ({
    productSub,
    setOpenSubscriptionModal,
    renderArr,
    updateKey,
    setValue,
    disabled = false,
}: {
    productSub: any;
    setOpenSubscriptionModal: any;
    renderArr: any;
    products: any;
    updateKey: any;
    setValue: any;
    disabled?: boolean;
}) => {
    return (
        <CustomModal
            noScroll
            open={true}
            title="Select Product Subscriptions"
            onClose={() => setOpenSubscriptionModal(false)}
            primaryAction={{
                content: "Alright",
                plain: true,
                onAction: () => setOpenSubscriptionModal(false),
            }}
            noClose={true}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    justifyContent: "center",
                    alignItems: "flex-start",
                }}
            >
                {productSub.subscriptions.map((plan: any) => (
                    <div key={plan.id}>
                        <Checkbox
                            label={plan.name}
                            value=""
                            checked={plan.checked}
                            disabled={disabled}
                            onChange={(val) => {
                                const temp: ProductObj[] = [...renderArr];
                                const idx = temp.findIndex((p) => p.id === productSub.id);

                                if (idx >= 0) {
                                    const original = temp[idx];
                                    const realProduct: ProductObj = {
                                        ...original,
                                        subscriptions: original.subscriptions.map((s: any) =>
                                            s.id === plan.id ? { ...s, checked: val } : s,
                                        ),
                                    };

                                    const areAllUnchecked = realProduct.subscriptions.every(
                                        (p: any) => p.checked === false,
                                    );
                                    if (areAllUnchecked) {
                                        realProduct.isSubscribed = false;
                                        realProduct.oneTimePurchase = true;
                                    }

                                    temp[idx] = realProduct;
                                    setValue(updateKey, temp, { shouldDirty: true });
                                }
                            }}
                        />
                    </div>
                ))}

                <p>If you do not select any of the above subscriptions, then this value gets checked</p>
                <Checkbox
                    label="One time purchase"
                    value=""
                    checked={productSub.oneTimePurchase}
                    disabled={disabled}
                    onChange={(val) => {
                        const temp: ProductObj[] = [...renderArr];
                        const idx = temp.findIndex((p) => p.id === productSub.id);

                        if (idx >= 0) {
                            const original = temp[idx];

                            if (original) {
                                const realProduct: ProductObj = { ...original };
                                const areAllUnchecked = productSub.subscriptions.every((p: any) => p.checked === false);
                                if (areAllUnchecked) {
                                    realProduct.isSubscribed = false;
                                    realProduct.oneTimePurchase = true;
                                } else {
                                    realProduct.oneTimePurchase = val;
                                    realProduct.isSubscribed = false;
                                }
                                temp[idx] = realProduct;
                                setValue(updateKey, temp, { shouldDirty: true });
                            }
                        }
                    }}
                />
            </div>
        </CustomModal>
    );
};
